import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';
import { collection, query, onSnapshot, getDocs } from 'firebase/firestore';

// Hook: useAssignmentsForLocation(locationId)
// Returns { map: { [employeeId]: nestedAssignmentsForLocation }, loading, error }
// The nestedAssignmentsForLocation shape matches the per-location slice of legacy employee.assignments[locationId]: { departments: { [deptId]: { roles: { [roleId]: { assigned, isPrimary, appearsOnSchedule, wage } } } } }

export default function useAssignmentsForLocation(locationId) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    (async () => {
      if (!locationId) {
        setMap({});
        setLoading(false);
        return;
      }
      try {
        const tenantId = await tryResolveTenantId();
        if (!tenantId) {
          setMap({});
          setLoading(false);
          return;
        }

  // We'll listen to all employees' assignments subcollections for this tenant
        // and filter by locationId. To avoid collectionGroup complexity in the browser,
        // we'll iterate employees and attach listeners per-employee. For medium-sized
        // tenants this is acceptable; if you expect thousands of employees, we can
        // switch to a server-side index or use a collectionGroup query with a dedicated index.

        const employeesCol = collection(db, `tenants/${tenantId}/employees`);
        const snap = await getDocs(employeesCol);
        const empIds = snap.docs.map(d => d.id);

  const localMap = {};

        // Helper to process single employee assignments
        const processEmployee = async (empId) => {
          try {
            const assignmentsCol = collection(db, `tenants/${tenantId}/employees`, empId, 'assignments');
            const q = query(assignmentsCol);
            const subUnsub = onSnapshot(q, s => {
              if (!mounted) return;
              const docs = s.docs.map(d => ({ id: d.id, ...d.data() }));
              const perLocation = {};
              // Build nested for only this locationId (or locationName as a fallback)
              const sel = String(locationId);
              docs.forEach(d => {
                // support callers that pass a location name instead of id
                if (!(String(d.locationId) === sel || String(d.locationName || '') === sel)) return;
                const locId = d.locationId;
                const deptId = d.departmentId || 'unknown_department';
                const roleId = d.roleId;
                if (!locId || !roleId) return;
                if (!perLocation.departments) perLocation.departments = {};
                if (!perLocation.departments[deptId]) perLocation.departments[deptId] = { roles: {} };
                perLocation.departments[deptId].roles[roleId] = {
                  assigned: typeof d.assigned === 'boolean' ? d.assigned : true,
                  isPrimary: !!d.isPrimary,
                  appearsOnSchedule: typeof d.appearsOnSchedule === 'boolean' ? d.appearsOnSchedule : true,
                  wage: typeof d.wage !== 'undefined' ? d.wage : null
                };
              });

              if (Object.keys(perLocation).length === 0) {
                // no assignments for this location
                if (localMap[empId]) delete localMap[empId];
              } else {
                localMap[empId] = perLocation;
              }

              setMap({ ...localMap });
            }, err => {
              console.warn('assignments per-employee listener error', err);
            });

            return subUnsub;
          } catch (err) {
            console.warn('processEmployee error', err);
            return null;
          }
        };

        const unsubList = [];
        for (const id of empIds) {
          const u = await processEmployee(id);
          if (typeof u === 'function') unsubList.push(u);
        }

        unsub = () => { unsubList.forEach(fn => { try { fn(); } catch (e) {} }); };

        setLoading(false);
      } catch (err) {
        console.error('useAssignmentsForLocation error', err);
        if (!mounted) return;
        setError(err);
        setLoading(false);
      }
    })();

    return () => { mounted = false; try { if (unsub) unsub(); } catch (e) {} };
  }, [locationId]);

  return { map, loading, error };
}
